/**
 * Parse InfluxDB annotated CSV format to extract time-series data points
 *
 * Expected CSV format:
 * #group,FALSE,FALSE,TRUE,TRUE,TRUE
 * #datatype,dateTime:RFC3339,double,string,string,tag
 * #default,,,,,
 * ,_time,_value,_field,_measurement,asset
 * ,2025-11-29T00:00:00Z,600,weight,reading,0f072cee-78e6-4fc4-b82b-5a976b0f3c5d
 * ...
 */

export interface ParsedDataPoint {
  date: string
  weight: number
  timestamp: number // Add timestamp in milliseconds for easier comparison
}

/**
 * Normalize date string to UTC timestamp for consistent comparison
 * @param dateStr Date string that may or may not have timezone info
 * @returns UTC timestamp in milliseconds
 */
function normalizeToUTC(dateStr: string): number {
  // If date has timezone info, use it; otherwise assume UTC
  return dateStr.includes("Z") || dateStr.includes("+") || dateStr.includes("-")
    ? new Date(dateStr).getTime()
    : new Date(dateStr + "Z").getTime() // Assume UTC for naive dates
}

export function parseInfluxCSV(csvContent: string): ParsedDataPoint[] {
  const lines = csvContent.split("\n")
  const dataPoints: ParsedDataPoint[] = []

  // Find the header row (starts with comma and contains _time, _value)
  let headerIndex = -1
  let timeIndex = -1
  let valueIndex = -1
  let fieldIndex = -1

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (line.startsWith(",_time")) {
      headerIndex = i
      const headers = line.split(",")
      timeIndex = headers.indexOf("_time")
      valueIndex = headers.indexOf("_value")
      fieldIndex = headers.indexOf("_field")
      break
    }
  }

  if (headerIndex === -1 || timeIndex === -1 || valueIndex === -1) {
    throw new Error("Invalid CSV format: Could not find required columns (_time, _value)")
  }

  // Parse data rows (after header)
  for (let i = headerIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line || line.startsWith("#")) continue

    const columns = line.split(",")
    if (columns.length <= Math.max(timeIndex, valueIndex, fieldIndex)) continue

    // Only process weight field data
    const field = columns[fieldIndex]
    if (field !== "weight") continue

    const timeStr = columns[timeIndex]
    const valueStr = columns[valueIndex]

    if (timeStr && valueStr) {
      const weight = parseFloat(valueStr)
      if (!isNaN(weight)) {
        const timestamp = new Date(timeStr).getTime()
        dataPoints.push({
          date: timeStr,
          weight: weight,
          timestamp: timestamp,
        })
      }
    }
  }

  // Sort by date to ensure chronological order
  dataPoints.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  return dataPoints
}

/**
 * Aggregate comparison data to match chart data (both historical and forecast) and filter out weight increases
 * @param comparisonData Raw comparison data points
 * @param historicalData Historical data points from the chart
 * @param forecastData Forecast data points to match intervals with
 * @param intervalHours Interval in hours (e.g., 4 for 4-hour intervals)
 * @returns Aggregated comparison data points aligned with chart timestamps
 */
export function aggregateComparisonData(
  comparisonData: ParsedDataPoint[],
  allChartData: any[], // Combined historical + forecast data
  intervalHours: number
): ParsedDataPoint[] {
  if (!comparisonData.length || !allChartData.length) return []

  // Filter out weight increases >5kg (likely refills)
  const filteredData = filterWeightIncreases(comparisonData, 5)

  // Get chart time range (both historical and forecast)
  const chartStart = normalizeToUTC(allChartData[0].date)
  const chartEnd = normalizeToUTC(allChartData[allChartData.length - 1].date)

  // Filter comparison data to chart time range
  const timeRangeFiltered = filteredData.filter((point) => point.timestamp >= chartStart && point.timestamp <= chartEnd)

  if (!timeRangeFiltered.length) return []

  // Use a more flexible interval for matching - at least 1 hour, but expand for larger intervals
  const intervalMs = Math.max(intervalHours * 60 * 60 * 1000, 60 * 60 * 1000) // At least 1 hour
  const aggregated: ParsedDataPoint[] = []

  // For each chart point, only include if we have actual comparison data within a reasonable time window
  allChartData.forEach((chartPoint) => {
    const chartTime = normalizeToUTC(chartPoint.date)

    // Use interval-aware time windows
    const timeWindow =
      intervalHours >= 24
        ? 12 * 60 * 60 * 1000 // ±12 hours for daily (covers full day)
        : Math.max((intervalHours * 60 * 60 * 1000) / 2, 30 * 60 * 1000) // ±interval/2, min 30min

    if (intervalHours >= 24) {
      // Daily forecast: use end-of-day logic
      const dayStart = new Date(chartTime).setUTCHours(0, 0, 0, 0)
      const dayEnd = dayStart + 24 * 60 * 60 * 1000

      const dayPoints = timeRangeFiltered.filter((point) => point.timestamp >= dayStart && point.timestamp < dayEnd)

      if (dayPoints.length > 0) {
        // Use the last reading of the day
        const lastReading = dayPoints[dayPoints.length - 1]
        aggregated.push({
          date: chartPoint.date, // Use exact chart date string (preserves original format)
          weight: lastReading.weight,
          timestamp: chartTime,
        })
      }
    } else {
      // Hourly forecast: use closest point logic with improved window
      const windowStart = chartTime - timeWindow
      const windowEnd = chartTime + timeWindow

      // Find comparison points within this specific time window
      const pointsInWindow = timeRangeFiltered.filter(
        (point) => point.timestamp >= windowStart && point.timestamp <= windowEnd
      )

      if (pointsInWindow.length > 0) {
        // Take the point closest to the chart timestamp
        const closest = pointsInWindow.reduce((prev, curr) =>
          Math.abs(curr.timestamp - chartTime) < Math.abs(prev.timestamp - chartTime) ? curr : prev
        )

        // Only include if the match is reasonably close (within the window)
        const timeDiff = Math.abs(closest.timestamp - chartTime)
        if (timeDiff <= timeWindow) {
          aggregated.push({
            date: chartPoint.date, // Use exact chart date string (preserves original format)
            weight: closest.weight,
            timestamp: chartTime,
          })
        }
      }
    }
  })

  // Debug logging removed for production

  return aggregated
}

/**
 * Filter out weight increases greater than the specified threshold (likely refills)
 * @param data Sorted comparison data points
 * @param thresholdKg Maximum allowed weight increase in kg
 * @returns Filtered data points with large increases removed
 */
function filterWeightIncreases(data: ParsedDataPoint[], thresholdKg: number): ParsedDataPoint[] {
  if (data.length <= 1) return data

  const filtered: ParsedDataPoint[] = [data[0]] // Always include first point

  for (let i = 1; i < data.length; i++) {
    const current = data[i]
    const previous = filtered[filtered.length - 1]
    const weightIncrease = current.weight - previous.weight

    // Only include points where weight decreased or increased by less than threshold
    if (weightIncrease <= thresholdKg) {
      filtered.push(current)
    }
    // Skip points with large weight increases (likely refills)
  }

  return filtered
}

export function readCSVFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      const result = event.target?.result
      if (typeof result === "string") {
        resolve(result)
      } else {
        reject(new Error("Failed to read file as text"))
      }
    }
    reader.onerror = () => reject(new Error("Failed to read file"))
    reader.readAsText(file)
  })
}
